<template>
  <div class="annual-report">
    <!-- 返回按钮 -->
    <div class="return-back-btn" @click="handleReturn">
      <img src="@/assets/images/common/icon-back2.png" alt="首页" />
    </div>

    <!-- 数据加载中 -->
    <div v-if="isDataLoading" class="loading-container">
      <div class="loading-content">
        <img class="loading-logo" src="@/assets/images/activity/logo.png" alt="logo" />
        <div class="loading-text">正在生成您的年度报告</div>
        <div class="loading-dots">
          <span class="dot"></span>
          <span class="dot"></span>
          <span class="dot"></span>
        </div>
      </div>
    </div>

    <!-- 数据加载完成后显示内容 -->
    <div v-else class="scroll-shell" ref="scrollShell">
      <div
        class="page-shell"
        v-for="page in pageFlow"
        :key="page.key"
      >
        <div
        >
          <template v-if="page.key === 'page-1'">
            <div class="page-canvas"
                 :class="[page.key, { 'text-animate': textAnimated[page.key] }]">
              <div class="page-brand" aria-hidden="true"></div>

              <div class="page-body">
                <div class="page1-greeting">
                  <div class="text-22 font-bold" data-text-seq>
                    @ <span class="text-red">{{ reportData.page1RealName }}</span>
                  </div>
                  <div class="text-22 font-hello font-bold" data-text-seq>你好</div>
                  <SmilingFace v-if="svgAnimated['page-1']" class="page1-spark"/>
                </div>

                <div class="text-14 font-medium " data-text-seq>
                  <div>
                    <span class="text-red">{{ reportData.page1RegisterYear }}</span> 年 <span class="text-red">{{ reportData.page1RegisterMonth }}</span> 月 <span class="text-red">{{ reportData.page1RegisterDay }}</span> 日 是我们相遇的第一天
                  </div>

                  <div class="m-b-16">
                    一转眼已经 <span class="text-red">{{ reportData.page1RegisterDays }}</span> 天
                  </div>
                  <div class="gradient-line"></div>
                </div>

                <div data-text-seq>
                  <div class="text-14 font-bold m-t-14 m-b-14">
                    你是 <span class="text-red">iEP 3.0</span> 第
                    <span class="text-red">{{ reportData.page1RegisterRank }}</span> 位伙伴
                  </div>
                  <div class="gradient-line"></div>
                </div>

                <div data-text-seq>
                  <div class="text-14 font-medium m-b-14 m-t-14">
                    <div>
                      你还记得 <span class="text-red">{{ reportData.page1FirstLearContent }}</span> 吗
                    </div>
                    <div>
                      是你在 <span class="text-red">iEP</span> 上学习的第一个内容
                    </div>
                  </div>
                  <div class="gradient-line"></div>
                </div>

                <div class="text-14 font-medium m-t-16" data-text-seq>
                  从那时起 我们彼此鼓舞 进阶成长
                </div>
              </div>

              <div class="page-footer">
                <div class="slide-btn">上滑继续</div>
                <div class="footer-meta right">
                  <div>
                    <div>022118-251216</div>
                    <div>本内容仅供医学专业人士本人参考</div>
                    <div>严禁截图、拍照、翻印、转载及传播</div>
                  </div>
                  <div>数据统计周期 2025.1.1-2025.12.31</div>
                </div>
              </div>
            </div>
          </template>

          <template v-else-if="page.key === 'page-2'">
            <div class="page-canvas"
                 :class="[page.key, { 'text-animate': textAnimated[page.key] }]">
              <div class="page-brand" aria-hidden="true"></div>

              <div class="page-body">
                <active v-if="svgAnimated['page-2']"/>
                <div class="text-22 font-hello font-medium m-t-28">
                  <div data-text-seq>你全年最活跃的时段</div>
                  <div class="text-22 font-medium" data-text-seq>
                    是 <span class="text-red">{{ reportData.page2MostActiveMonth }}</span> 月
                  </div>
                </div>
                <div data-text-seq>
                  <div class="text-14 font-medium m-t-28 m-b-14">2025年 有几个日子我们记得格外清晰</div>

                  <div class="gradient-line"></div>
                </div>

                <div data-text-seq v-if="isLateNightTime(reportData.page2Time1Hour) || isEarlyMorningTime(reportData.page2Time1Hour)">
                  <div class="page2-block">
                    <div class="text-14 font-bold">
                      <span class="text-red">{{ reportData.page2Date1Month }}</span> 月 <span class="text-red">{{ reportData.page2Date1Day }}</span> 日
                    </div>
                    <!-- 22点到5点（不包括5点）：显示深夜文案 -->
                    <template v-if="isLateNightTime(reportData.page2Time1Hour)">
                      <div class="text-14 font-medium">
                        <span class="text-red">{{ reportData.page2Time1Hour }}</span> 点 <span v-if="reportData.page2Time1Minute" class="text-red">{{ reportData.page2Time1Minute }}</span> 分你很晚还没睡
                      </div>
                      <div class="text-14 font-medium">你总在向着目标默默努力</div>
                    </template>
                    <!-- 5点到9点：显示清晨文案 -->
                    <template v-else-if="isEarlyMorningTime(reportData.page2Time1Hour)">
                      <div class="text-14 font-medium">
                        你清晨 <span class="text-red">{{ reportData.page2Time1Hour }}</span> 点 <span v-if="reportData.page2Time1Minute" class="text-red">{{ reportData.page2Time1Minute }}</span> 分就已上线
                      </div>
                      <div class="text-14 font-medium">在他人醒来之前 已向着目标迈进一步</div>
                    </template>
                  </div>

                  <div class="gradient-line"></div>
                </div>

                <div data-text-seq>
                  <div class="page2-block">
                    <div class="text-14 font-bold">
                      <span class="text-red">{{ reportData.page2Date2Month }}</span> 月 <span class="text-red">{{ reportData.page2Date2Day }}</span> 日
                    </div>
                    <div class="text-14 font-medium">
                      你将一门内容反复学习了 <span class="text-red">{{ reportData.page2RepeatLearnTimes }}</span> 遍
                    </div>
                    <div class="text-14 font-medium">循环的是内容 更新的是理解</div>
                  </div>

                  <div class="gradient-line"></div>
                </div>

                <div class="page2-block" data-text-seq>
                  <div class="text-14 font-bold m-b-2">
                    <span class="text-red">{{ reportData.page2LastLoginMonth }}</span> 月 <span class="text-red">{{ reportData.page2LastLoginDay }}</span> 日
                  </div>
                  <div class="text-14 font-medium">你完成了最后一次学习</div>
                  <div class="text-14 font-medium">为这一年 画下圆满的句点</div>
                </div>
              </div>

              <div class="page-footer">
                <div class="slide-btn">上滑继续</div>
                <div class="footer-meta right">
                  <div>
                    <div>022118-251216</div>
                    <div>本内容仅供医学专业人士本人参考</div>
                    <div>严禁截图、拍照、翻印、转载及传播</div>
                  </div>
                  <div>数据统计周期 2025.1.1-2025.12.31</div>
                </div>
              </div>
            </div>
          </template>

          <template v-else-if="page.key === 'page-3'">
            <div class="page-canvas"
                 :class="[page.key, { 'text-animate': textAnimated[page.key] }]">
              <div class="page-brand" aria-hidden="true"></div>

              <div class="page-body">

                <div class="page3-content">
                  <trajectory v-if="svgAnimated['page-3']" class="page3-circle"/>
                  <trajectoryStar1 v-if="svgAnimated['page-3']" class="page3-Star1"/>
                  <trajectoryStar2 v-if="svgAnimated['page-3']" class="page3-Star2"/>
                  <trajectoryStar3 v-if="svgAnimated['page-3']" class="page3-Star3"/>
                  <trajectoryLine v-if="svgAnimated['page-3']" class="page3-Line"/>
                  <div class="text-22 font-hello font-bold l-n-1" data-text-seq>2025年</div>
                  <div class="text-22 font-hello text-mr l-n-1 m-t-2" data-text-seq>你的学习轨迹</div>

                  <div data-text-seq>
                    <div>
                      <div class="text-14 font-bold m-t-30 m-b-3">
                        你使用了 <span class="text-red">{{ reportData.page3AiSearchTimes }}</span> 次智能搜索
                      </div>
                      <div class="text-14 font-medium m-b-14">
                        有 <span class="text-red">{{ reportData.page3AiSearchSameContentUser }}</span> 人和你搜索了同样的内容
                      </div>
                    </div>
                    <div class="gradient-line"></div>
                  </div>
                  <div data-text-seq>
                    <div class="page3-card">
                      <div class="text-14 font-bold m-t-14 m-b-3">
                        你特别关注 <span class="text-red">{{ reportData.page3FollowContentTag }}</span> 的内容
                      </div>
                      <div class="text-14 font-medium m-b-14">
                        <span class="text-red">{{ reportData.page3FollowContentRelatedUserRatio }}</span> 的学习都与之相关
                      </div>
                    </div>

                    <div class="gradient-line"></div>
                  </div>

                  <div class="text-14 font-bold m-t-14" data-text-seq>
                    <div>你看了只有 <span class="text-red">{{ reportData.page3MinLearnTimesUsers }}</span> 人看过的小众宝藏</div>
                    <div class="text-red">{{ reportData.page3MinLearnContent }}</div>
                  </div>
                </div>
              </div>

              <div class="page-footer">
                <div class="slide-btn">上滑继续</div>
                <div class="footer-meta left">
                  <div>
                    <div>022118-251216</div>
                    <div>本内容仅供医学专业人士本人参考</div>
                    <div>严禁截图、拍照、翻印、转载及传播</div>
                  </div>
                  <div>数据统计周期 2025.1.1-2025.12.31</div>
                </div>
              </div>
            </div>
          </template>

          <template v-else-if="page.key === 'page-4'">
            <div class="page-canvas"
                 :class="[page.key, { 'text-animate': textAnimated[page.key] }]">
              <img class="page-brand-img" src="@/assets/images/activity/logo.png" alt="" aria-hidden="true"/>

              <div class="page-body">
                <div class="page4-header-box">
                  <rankingList v-if="svgAnimated['page-4']" class="page4-rank animate__animated animate__fadeInUp"/>
                  <top v-if="svgAnimated['page-4']" class="page4-rank-top animate__animated animate__fadeInUp"/>
                  <div class="page4-title-row">
                    <div class="text-22 font-hello" data-text-seq>你的2025</div>
                  </div>
                  <div class="text-22 font-hello text-mr l-n-12" data-text-seq>年度内容榜单</div>
                </div>

                <div data-text-seq>
                  <div class="page4-rank-list text-14 font-medium text-red">
                    <div v-for="(item, index) in reportData.page4LearnContentTop3" :key="index">{{ index + 1 }}. {{ item.title }}</div>
                  </div>
                  <div class="gradient-line"></div>
                </div>
                <div data-text-seq v-if="reportData.page4learnCourseTop1 && reportData.page4learnCourseTop1 !== '-'">
                  <div class="page4-card">
                    <div class="text-14 font-medium">
                      <span class="text-red">「{{ reportData.page4learnCourseTop1 }}」</span>是你年度学习最长时间的课程
                    </div>
                    <div class="text-14 font-medium">
                      从 <span class="text-red">{{ reportData.page4learnCourseTop1FirstDate }}</span> 开始
                    </div>
                    <div class="text-14 font-medium">
                      累计学习了 <span class="text-red">{{ reportData.page4LearnCourseTimesTop1 }}</span> 次
                    </div>
                    <div class="page4-course-fav">
                    <span class="text-14 font-medium">
                      <span class="text-red">{{ reportData.page4LearnCourseLikeUsers }}</span> 位医生的年度最爱
                    </span>
                      <span class="text-14 font-medium">也是 TA</span>
                    </div>
                  </div>
                  <div class="gradient-line"></div>
                </div>
                <div data-text-seq>
                  <div class="page4-card2">
                    <div class="text-14 font-medium">这些偶然的相遇</div>
                    <div class="text-14 font-medium">都让专业的道路 多一重温度</div>
                  </div>
                  <div class="gradient-line"></div>
                </div>
                <div class="page4-card2 m-b-140" data-text-seq>
                  <div class="text-14 font-medium">感谢你</div>
                  <div class="text-14 font-medium">
                    让 <span class="text-red">iEP</span> 陪你一起走过这些时刻
                  </div>
                </div>
              </div>

              <div class="page-footer">
                <div class="slide-btn">上滑继续</div>
                <div class="footer-meta left color-fff">
                  <div>
                    <div>022118-251216</div>
                    <div>本内容仅供医学专业人士本人参考</div>
                    <div>严禁截图、拍照、翻印、转载及传播</div>
                  </div>
                  <div>数据统计周期 2025.1.1-2025.12.31</div>
                </div>
              </div>
            </div>
          </template>

          <template v-else-if="page.key === 'page-5'">
            <div class="page-canvas"
                 :class="[page.key, { 'text-animate': textAnimated[page.key] }]">
              <div class="page-brand" aria-hidden="true"></div>

              <div class="page-body">
                <div class="page5-title-row">
                  <div v-if="svgAnimated['page-5']" class="text-14 font-hello m-t-24 text-box page5-line-one"
                       data-text-seq>
                    在学习的旷野里
                    <wilderness class="page5-vector-img"/>
                  </div>
                  <div v-if="svgAnimated['page-5']" class="text-22 font-hello l-n-12 text-box page5-line-two"
                       data-text-seq>
                    你发现了新大陆
                    <wildernessStar class="page5-vector-Star"/>
                  </div>
                </div>
                <div data-text-seq>
                  <div class="text-14 font-medium m-b-14">
                    25年你一共邂逅了 <span class="text-red">{{ reportData.page5LearnAuthorCount }}</span> 位作者
                  </div>

                  <div class="gradient-line"></div>
                </div>

                <div data-text-seq>
                  <div>
                    <div class="text-14 font-bold m-t-14 m-b-12">
                      <span class="text-red">{{ reportData.page5LearnMaxAuthorName }}</span> 是你的25年关注度最高的作者
                    </div>
                    <div class="text-14 font-medium m-b-14">
                      你是 TA 当之无愧的 <span class="text-red">EP Fans</span>
                    </div>
                  </div>

                  <div class="gradient-line"></div>
                </div>

                <div data-text-seq>
                  <div class="text-14 font-medium m-t-14 m-b-12">
                    你一共看TA的课 <span class="text-red">{{ reportData.page5MaxAuthorLearnDays }}</span> 天
                    <span class="text-red">{{ reportData.page5MaxAuthorLearnTimes }}</span> 次共
                    <span class="text-red">{{ reportData.page5MaxAuthorLearnMinutes }}</span> 分钟
                  </div>
                  <div class="text-14 font-medium">因为你的支持 TA的分享才更完整</div>
                </div>
              </div>

              <div class="page-footer">
                <div class="slide-btn">上滑继续</div>
                <div class="footer-meta left">
                  <div>
                    <div>022118-251216</div>
                    <div>本内容仅供医学专业人士本人参考</div>
                    <div>严禁截图、拍照、翻印、转载及传播</div>
                  </div>
                  <div>数据统计周期 2025.1.1-2025.12.31</div>
                </div>
              </div>
            </div>
          </template>

          <template v-else-if="page.key === 'page-6'">
            <div class="page-canvas"
                 :class="[page.key, { 'text-animate': textAnimated[page.key] }]">
              <div class="page-brand" aria-hidden="true"></div>

              <div class="page6-body">
                <div class="page6-content">
                  <div class="page6-title m-b-30">
                    <div class="text-22 font-hello font-bold" data-text-seq>看看2025</div>
                    <div class="page6-title-row">
                      <div class="text-22 font-hello font-bold p-r" data-text-seq>
                        你的学习记忆吧！
                        <memory v-if="svgAnimated['page-6']" class="page6-vector-img"/>
                      </div>
                    </div>
                  </div>
                  <div data-text-seq>
                    <div class="text-14 font-medium m-b-14 gap-6">
                      <div>感谢你的每一次驻足</div>
                      <div>
                        与 <span class="text-red">{{ reportData.page6IepRegisterCount }}</span> 位医生一起
                      </div>
                      <div>
                        汇聚成 <span class="text-red">iEP3.0</span> 的 2025年
                      </div>
                    </div>

                    <div class="gradient-line"></div>
                  </div>

                  <div class="text-14 font-medium m-t-14 gap-3" data-text-seq>
                    <div>这一年 我们共同迎来</div>
                    <div>
                      <span class="text-red">{{ reportData.page6LearnCount }}</span> 份新的学习内容
                    </div>
                  </div>
                </div>

                <div class="page6-board" data-text-seq>
                  <div class="page6-row">
                    <div class="page6-label">
                      <div>年度</div>
                      <div class="font-bold">大事件</div>
                    </div>
                    <div class="page6-cards">
                      <img class="page6-card-1" src="@/assets/images/activity/annualMajorEvent1.png" alt="年度大事件1" @click="goToKdSimulator" v-track="{ functionName: `年度大事件-模拟器下载页` }"/>
                      <img class="page6-card-2" src="@/assets/images/activity/annualMajorEvent2.png" alt="年度大事件2" @click="goToECGenius" v-track="{ functionName: `年度大事件-室早助手页` }"/>
                    </div>
                  </div>

                  <div class="page6-row">
                    <div class="page6-label">
                      <div>年度</div>
                      <div class="font-bold">内容</div>
                    </div>
                    <div class="page6-cards">
                      <div v-for="(item, index) in sortedPage6LearnContent" :key="index" class="page6-card2-box" @click="goToLearnContent(item)" v-track="{ functionName: `年度内容-${item.title}` }">
                        <img class="page6-card2" :src="getContentImage(item.type)" alt=""/>
                        <div class="page6-card-meta">阅读{{ item.num }}次</div>
                      </div>
                    </div>
                  </div>

                  <div class="page6-row">
                    <div class="page6-label">
                      <div>年度</div>
                      <div class="font-bold">作者</div>
                    </div>
                    <div class="page6-cards">
                      <div v-for="(author, index) in reportData.page6LearnAuthorTop3" :key="index" class="page6-author">
                        <img v-if="author.headPic" class="page6-avatar" :src="author.headPic" alt=""/>
                        <div v-else class="page6-avatar"></div>
                        <div class="page6-author-name">{{ author.authorName }}</div>
                        <div class="page6-author-meta">{{ author.authorHospitalName }}</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div class="page-footer">
                <div class="slide-btn">上滑继续</div>
                <div class="footer-meta left color-fff">
                  <div>
                    <div>022118-251216</div>
                    <div>本内容仅供医学专业人士本人参考</div>
                    <div>严禁截图、拍照、翻印、转载及传播</div>
                  </div>
                  <div>数据统计周期 2025.1.1-2025.12.31</div>
                </div>
              </div>
            </div>
          </template>

          <template v-else-if="page.key === 'page-new-user'">
            <div class="page-canvas" ref="newUserSharePage"
                 :class="[page.key, { 'text-animate': textAnimated[page.key] }]">
              <div class="page-new-user-share-content">
                <img class="page-brand-img" src="@/assets/images/activityLogo.png" alt="" aria-hidden="true"/>

                <div class="page-body pd-r-20">
                  <div class="page-new-user-content">
                    <div data-text-seq>
                      <div class="text-14 font-medium m-b-14 new-user-box" >
                        <div>iEP是你的前沿知识库</div>
                        <div>权威课程、经典病例、专业文献…</div>
                        <div>你需要的 尽在这里</div>
                      </div>

                      <div class="gradient-line"></div>
                    </div>

                   <div data-text-seq>
                     <div class="text-14 font-medium m-t-14 m-b-14 new-user-box" >
                       <div>iEP是你的专属充电站</div>
                       <div>结合你的兴趣、定制最合适的内容</div>
                       <div>每一次出发 都有前行的方向</div>
                     </div>

                     <div class="gradient-line"></div>
                   </div>

                    <div class="text-14 font-medium m-t-14 m-b-14" data-text-seq>
                      <div>来iEP和电生理领域一同成长</div>
                    </div>

                    <img class="new-user-qr" src="@/assets/images/annualReportQR.png" alt="二维码"/>

                  </div>
                </div>

                <div class="new-user-bottom-tip">
                  扫码查看你的2025学习报告！
                </div>
              </div>

              <div class="page-footer m-b-16" data-text-seq>
                <div class="slide-btn"
                     @touchstart="onSavePressStart"
                     @touchend="onSavePressEnd"
                     @touchcancel="onSavePressEnd"
                     @mousedown="onSavePressStart"
                     @mouseup="onSavePressEnd">长按保存图片</div>
                <div class="footer-meta right m-r-25">
                  <div>
                    <div>022118-251216</div>
                    <div>内容仅代表专家个人观点</div>
                    <div>仅供医疗专业人士参考</div>
                  </div>
                  <div>数据统计周期 2025.1.1-2025.12.31</div>
                </div>
              </div>
            </div>
          </template>

          <template v-else-if="page.key === 'page-7'">
            <div class="page-canvas" ref="sharePage"
                 :class="[page.key, { 'text-animate': textAnimated[page.key] }]">
              <div  class="page-7-content">
                <img class="page-brand-img" src="@/assets/images/activity/logo.png" alt="" aria-hidden="true"/>
                <div class="page-body page7-body">
                  <div class="page7-title-row">
                    <div class="page7-badge p-r font-bold" data-text-seq>
                      <img v-if="svgAnimated['page-7']" class="page7-vector-img"
                           src="@/assets/images/activity/decoration1.png" alt="" aria-hidden="true"/>
                      <img v-if="svgAnimated['page-7']" class="page7-vector-img2"
                           src="@/assets/images/activity/decoration2.png" alt="" aria-hidden="true"/>
                      重磅来袭！
                    </div>

                    <div class="text-16 font-bold" data-text-seq>
                      <span class="page7-mention m-r-25">@ <span class="text-red">{{ reportData.page1RealName }}</span></span>现在揭晓你的2025年度之最
                    </div>
                  </div>

                  <div class="page7-grid">
                    <div data-text-seq>
                      <div class="page7-metrics">
                        <div class="text-14 font-medium m-b-6">
                          你 一共学习了
                        </div>
                        <div class="page7-metric-list m-b-14">
                          <div class="page7-metric-list_item">
                            <div class="f-w"><span class="text-red">{{ reportData.page7CourseCount }}</span> 门课程</div>
                            <div class="f-w"><span class="text-red">{{ reportData.page7LiveCount }}</span> 场直播</div>
                            <div class="f-w"><span class="text-red">{{ reportData.page7CaseCount }}</span> 份病例</div>
                          </div>
                          <div class="page7-metric-list_item">
                            <div class="f-w"><span class="text-red">{{ reportData.page7CampCount }}</span> 期训练营</div>
                            <div class="f-w"><span class="text-red">{{ reportData.page7PaperCount }}</span> 篇文献</div>
                            <div class="f-w"><span class="text-red">{{ reportData.page7QuestionCount }}</span> 次心电图答题
                            </div>
                          </div>
                        </div>
                        <div class="page7-metric-extra m-b-14">
                          <div>年度学习时长 <span class="text-red">{{ reportData.page7YearlyLearningTime }}</span> 分钟</div>
                          <div>年度课程 <span class="text-red">{{ reportData.page7YearlyCourse }}</span></div>
                          <div>年度最爱作者 <span class="text-red">{{ reportData.page5LearnMaxAuthorName }}</span></div>
                        </div>
                      </div>
                      <div class="gradient-line"></div>
                    </div>
                    <div data-text-seq>
                      <div class="page7-summary m-b-14 m-t-14">
                        <div>年度学习关键词 <span class="text-red">{{ reportData.page7YearlyTag }}</span></div>
                        <div>与 iEP 3.0 见面 <span class="text-red">{{ reportData.page7LoginCount }}</span> 次</div>
                        <div>获得 <span class="text-red">{{ reportData.page7UserTotalCost }}</span> 积分，超越平台
                          <span class="text-red">{{ reportData.page7UserCostRank }}</span> 用户
                        </div>
                      </div>
                      <div class="gradient-line"></div>
                    </div>

                  </div>
                  <div class="page7-grid" data-text-seq>
                    <div class="page7-cta-wrapper m-t-14 m-b-14">
                      <div class="page7-cta">
                        <div>准备好了吗</div>
                        <div>开启你2026</div>
                        <div>独一无二的 iEP 3.0 学习</div>
                      </div>
                      <img class="page7-qr" src="@/assets/images/annualReportQR.png" alt="二维码"/>
                    </div>
                    <div class="gradient-line"></div>
                  </div>

                </div>
              </div>
              <div class="page7-grid m-0 page-7-pd medal-box" data-text-seq>
                <img src="@/assets/images/activity/badge.png" class="m-t-14 medal"/>
                <div class="m-t-14 shareText">
                  <div>保存图片发送朋友圈并配文</div>
                  <div>2026 我们一起学 分享你的年度报告</div>
                  <div>即可获得<span class="font-bold">iEP 3.0平台限定勋章1枚</span></div>
                  <div>活动截至{{ activityEndDate ? $moment(activityEndDate).format('YYYY年M月D日') : '2026年3月31日' }}</div>
                </div>
              </div>
              <div class="page7-bottom-tip">
                扫码查看你的2025学习报告！
              </div>
              <div class="page-footer m-b-140 m-t-10" data-text-seq>
                <div class="slide-btn" @touchstart="onSavePressStart"
                     @touchend="onSavePressEnd"
                     @touchcancel="onSavePressEnd"
                     @mousedown="onSavePressStart"
                     @mouseup="onSavePressEnd">长按保存图片
                </div>
              </div>
              <div class="page-footer m-b-16">
                <div class="slide-btn" style="background: unset"></div>
                <div class="footer-meta right m-r-25 color-fff">
                  <div>
                    <div>022118-251216</div>
                    <div>内容仅代表专家个人观点</div>
                    <div>仅供医疗专业人士参考</div>
                  </div>
                  <div>数据统计周期 2025.1.1-2025.12.31</div>
                </div>
              </div>
            </div>
          </template>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import SmilingFace from './svgComponent/SmilingFace.vue'
import active from './svgComponent/active.vue'
import trajectory from './svgComponent/trajectory.vue'
import TrajectoryStar1 from './svgComponent/trajectoryStar1.vue'
import trajectoryStar2 from './svgComponent/trajectoryStar2.vue'
import trajectoryStar3 from './svgComponent/trajectoryStar3.vue'
import trajectoryLine from './svgComponent/trajectoryLine.vue'
import rankingList from './svgComponent/rankingList.vue'
import top from './svgComponent/top.vue'
import wilderness from './svgComponent/wilderness.vue'
import wildernessStar from './svgComponent/wildernessStar.vue'
import memory from './svgComponent/memory.vue'
import {getAnnualReport, getRegisterRank} from '@/api/api'
import {getShareInfo} from '@/utils/wxShare'
import withRouteTracking from '@/utils/withRouteTracking'
import html2canvas from 'html2canvas'
import {postRemind} from '@/api/simulator'
import {handleTraceFunction} from '@/utils/trace'
import {isAnnualReportLive, getAnnualReportDates} from '@/utils/annualReport'
import {setSha} from '../../utils/util'
const TEXT_ANIMATION_DURATION = 0.6
const TEXT_ANIMATION_GAP = 0.1

const UserProfile = {
  name: 'annualReport',
  components: {
    SmilingFace,
    active,
    trajectory,
    trajectoryStar1: TrajectoryStar1,
    trajectoryStar2: trajectoryStar2,
    trajectoryStar3: trajectoryStar3,
    trajectoryLine: trajectoryLine,
    rankingList: rankingList,
    top: top,
    wilderness: wilderness,
    wildernessStar: wildernessStar,
    memory: memory,
  },
  data() {
    return {
      reportData: {
        // Page 1 数据
        page1RealName: '-',
        page1RegisterYear: '-',
        page1RegisterMonth: '-',
        page1RegisterDay: '-',
        page1RegisterDays: '-',
        page1RegisterRank: '-',
        page1FirstLearContent: '-',

        // Page 2 数据
        page2MostActiveMonth: '-',
        page2Date1Month: '-',
        page2Date1Day: '-',
        page2Date2Month: '-',
        page2Date2Day: '-',
        page2Time1Hour: '-',
        page2Time1Minute: '-',
        page2RepeatLearnTimes: '-',
        page2LastLoginMonth: '-',
        page2LastLoginDay: '-',

        // Page 3 数据
        page3AiSearchTimes: '-',
        page3AiSearchSameContentUser: '-',
        page3FollowContentTag: '-',
        page3FollowContentRelatedUserRatio: '-',
        page3MinLearnTimesUsers: '-',
        page3MinLearnContent: '',

        // Page 4 数据
        page4LearnContentTop3: [],
        page4learnCourseTop1: '-',
        page4learnCourseTop1FirstDate: '-',
        page4LearnCourseTimesTop1: '-',
        page4LearnCourseLikeUsers: '-',

        // Page 5 数据
        page5LearnAuthorCount: '-',
        page5LearnMaxAuthorName: '-',
        page5MaxAuthorLearnDays: '-',
        page5MaxAuthorLearnTimes: '-',
        page5MaxAuthorLearnMinutes: '-',

        // Page 6 数据
        page6IepRegisterCount: '-',
        page6LearnCount: '-',
        page6DingweiCount: '-',
        page6XuniCount: '-',
        page6LearnContent: [],
        page6LearnAuthorTop3: [],

        // Page 7 数据
        page7CourseCount: '-',
        page7LiveCount: '-',
        page7CaseCount: '-',
        page7CampCount: '-',
        page7PaperCount: '-',
        page7QuestionCount: '-',
        page7YearlyLearningTime: '-',
        page7YearlyCourse: '-',
        page7YearlyTag: '-',
        page7LoginCount: '-',
        page7UserTotalCost: '-',
        page7UserCostRank: '-'
      },
      pointsTotal: null,
      isNewUser: false,
      activePageIndex: 0,
      svgAnimated: {
        'page-1': false,
        'page-2': false,
        'page-3': false,
        'page-4': false,
        'page-5': false,
        'page-6': false,
        'page-7': false,
        'page-new-user': false
      },
      textAnimated: {
        'page-1': false,
        'page-2': false,
        'page-3': false,
        'page-4': false,
        'page-5': false,
        'page-6': false,
        'page-7': false,
        'page-new-user': false
      },
      wheelLock: false,
      savePressTimer: null,
      scrollTimer: null,
      isGeneratingImage: false,
      isDataLoading: true,
      activityEndDate: null, // 活动截止日期
      contentImages: {
        1: require('@/assets/images/activity/annualContent1.png'),
        2: require('@/assets/images/activity/annualContent3.png'),
        3: require('@/assets/images/activity/annualContent2.png')
      }
    }
  },
  computed: {
    userInfo () {
      return this.$store.getters.userInfo
    },
    pageFlow() {
      // 新用户只显示两页
      if (this.isNewUser) {
        return [
          {key: 'page-1', show: true},
          {key: 'page-new-user', show: true}
        ].filter(item => item.show)
      }

      // 老用户显示完整流程
      return [
        {key: 'page-1', show: true},
        {key: 'page-2', show: true},
        {key: 'page-3', show: true},
        {key: 'page-4', show: true},
        {key: 'page-5', show: true},
        {key: 'page-6', show: true},
        {key: 'page-7', show: true}
      ].filter(item => item.show)
    },
    sortedPage6LearnContent() {
      // 调整顺序：课程(type=1) -> 文献(type=3) -> 病例(type=2)
      const content = this.reportData.page6LearnContent || []
      const sorted = [...content].sort((a, b) => {
        const order = {1: 1, 3: 2, 2: 3}
        return (order[a.type] || 999) - (order[b.type] || 999)
      })
      return sorted
    }
  },
  watch: {
    activePageIndex: {
      immediate: true,
      handler(nextIndex) {
        this.markSvgAnimatedByIndex(nextIndex)
        this.markTextAnimatedByIndex(nextIndex)
      }
    }
  },
  async mounted() {
    await this.fetchReport()
    this.initShare()
    this.$nextTick(() => {
      this.bindScroll()
      // 恢复滚动位置（如果有保存的位置）
      this.restoreScrollPosition()
    })
  },
  beforeDestroy() {
    this.unbindScroll()
    // 清理定时器
    if (this.scrollTimer) {
      clearTimeout(this.scrollTimer)
    }
  },
  methods: {
    /**
     * 将秒转换为分钟（向上取整）
     * @param {number|string} seconds - 秒数
     * @returns {number|string} - 转换后的分钟数，如果无效则返回 '-'
     */
    convertSecondsToMinutes(seconds) {
      const num = Number(seconds)
      return (num > 0 && !isNaN(num)) ? Math.ceil(num / 60) : '-'
    },
    /**
     * 判断是否是深夜时段（22点到5点，不包括5点）
     * @param {string|number} hour - 小时数
     * @returns {boolean}
     */
    isLateNightTime(hour) {
      if (!hour || hour === '-') return false
      const h = Number(hour)
      if (isNaN(h)) return false
      return h >= 22 || h < 5
    },
    /**
     * 判断是否是清晨时段（5点到9点）
     * @param {string|number} hour - 小时数
     * @returns {boolean}
     */
    isEarlyMorningTime(hour) {
      if (!hour || hour === '-') return false
      const h = Number(hour)
      if (isNaN(h)) return false
      return h >= 5 && h < 9
    },
    markSvgAnimatedByIndex(index) {
      const page = this.pageFlow[index]
      if (!page) return
      const key = page.key
      if (!Object.prototype.hasOwnProperty.call(this.svgAnimated, key)) return
      if (this.svgAnimated[key]) return
      this.svgAnimated[key] = true
    },
    markTextAnimatedByIndex(index) {
      const page = this.pageFlow[index]
      if (!page) return
      const key = page.key
      if (!Object.prototype.hasOwnProperty.call(this.textAnimated, key)) return
      if (this.textAnimated[key]) return
      this.$nextTick(() => {
        const success = this.applyTextSequence(key)
        // 只有成功设置了动画延迟，才标记为已完成
        if (success) {
          this.textAnimated[key] = true
        }
      })
    },
    applyTextSequence(key) {
      if (!this.$el) return false
      const pageEl = this.$el.querySelector(`.page-canvas.${key}`)
      if (!pageEl) return false
      const step = TEXT_ANIMATION_DURATION + TEXT_ANIMATION_GAP
      const items = pageEl.querySelectorAll('[data-text-seq]')
      items.forEach((item, index) => {
        item.style.setProperty('--text-delay', `${index * step}s`)
      })
      return true
    },
    onSavePressStart() {
      this.savePressTimer = setTimeout(() => {
        this.generateShareImage()
      }, 600)
    },

    onSavePressEnd() {
      clearTimeout(this.savePressTimer)
      this.savePressTimer = null
    },

    async generateShareImage() {
      if (this.isGeneratingImage) return

      this.isGeneratingImage = true

      // 记录海报生成行为
      handleTraceFunction({
        functionName: '2025年度报告海报生成',
        functionUrl: `${process.env.baseURL}${process.env.BASE_URL}/annualReport`,
        createTime: new Date().getTime()
      })

      const startTime = Date.now()

      // 彻底清理所有可能的遮罩层和 toast
      this.$toast.clear()

      this.$toast.loading({
        message: '正在生成图片...',
        forbidClick: true,
        duration: 0
      })

      let bgImageElement = null // 在 try 块外声明，确保 catch 块能访问

      try {
        // 根据当前页面获取要截图的元素
        const currentPage = this.pageFlow[this.activePageIndex]
        let el = null

        if (currentPage.key === 'page-7') {
          el = this.$refs.sharePage && this.$refs.sharePage[0]
        } else if (currentPage.key === 'page-new-user') {
          el = this.$refs.newUserSharePage
          // 如果是数组，取第一个元素
          if (Array.isArray(el)) {
            el = el[0]
          }
        }

        if (!el) {
          this.$toast.clear()
          this.$toast.fail('生成失败，请重试')
          return
        }

        // 保存当前滚动位置
        const originalScrollTop = el.scrollTop

        // 将页面滚动到顶部，确保截图从顶部开始
        el.scrollTop = 0

        // 创建遮罩层，防止用户看到二维码显示过程
        const mask = document.createElement('div')
        mask.style.position = 'fixed'
        mask.style.top = '0'
        mask.style.left = '0'
        mask.style.width = '100vw'
        mask.style.height = '100vh'
        mask.style.background = '#fff'
        mask.style.zIndex = '9999998'
        document.body.appendChild(mask)

        // 等待图片加载
        await this.waitImagesLoaded(el)

        // 临时移除文字动画效果，确保文字显示
        const textElements = el.querySelectorAll('[data-text-seq]')
        textElements.forEach(elem => {
          elem.style.opacity = '1'
          elem.style.animation = 'none'
        })

        // 获取视口高度
        const viewportHeight = window.innerHeight

        // 设置元素高度（第七页需要额外增加底部留白）
        let elementHeight = viewportHeight
        if (currentPage.key === 'page-7') {
          // 第七页增加底部留白，参考 m-b-140 的样式（140px）
          elementHeight = viewportHeight + 160 // 增加 160px 底部空间
        }

        // 保存原始背景图样式
        const originalBackgroundImage = el.style.backgroundImage

        // 临时移除 CSS 背景图，避免与插入的 img 元素重叠
        el.style.backgroundImage = 'none'
        el.style.height = `${elementHeight}px`
        el.style.minHeight = `${elementHeight}px`
        el.style.position = 'relative'

        // 使用 img 元素代替 CSS 背景图，确保高清显示
        let bgImageUrl = ''

        if (currentPage.key === 'page-7') {
          bgImageUrl = require('@/assets/images/activity/iEP-7.jpg')
        } else if (currentPage.key === 'page-new-user') {
          bgImageUrl = require('@/assets/images/activity/iEP-2.jpg')
        }

        if (bgImageUrl) {
          // 创建背景图 img 元素
          bgImageElement = document.createElement('img')
          bgImageElement.src = bgImageUrl
          bgImageElement.style.position = 'absolute'
          bgImageElement.style.top = '0'
          bgImageElement.style.left = '0'
          bgImageElement.style.width = '100%'
          bgImageElement.style.height = `${elementHeight}px` // 使用计算后的高度
          bgImageElement.style.objectFit = 'cover'
          bgImageElement.style.objectPosition = 'center top' // 从顶部对齐，避免裁剪重要内容
          bgImageElement.style.zIndex = '-1'
          bgImageElement.style.pointerEvents = 'none'

          // 插入到元素最前面
          el.insertBefore(bgImageElement, el.firstChild)

          // 等待背景图加载完成
          await new Promise((resolve, reject) => {
            if (bgImageElement.complete) {
              resolve()
            } else {
              bgImageElement.onload = resolve
              bgImageElement.onerror = resolve
            }
          })
        }

        // 临时显示二维码（只在截图时显示）
        const qrSelector = currentPage.key === 'page-7' ? '.page7-qr' : '.new-user-qr'
        const qrElement = el.querySelector(qrSelector)
        if (qrElement) {
          qrElement.style.display = 'block'
        }

        // 临时显示底部提示文字（只在截图时显示）
        const tipSelector = currentPage.key === 'page-7' ? '.page7-bottom-tip' : '.new-user-bottom-tip'
        const bottomTipElement = el.querySelector(tipSelector)
        if (bottomTipElement) {
          bottomTipElement.style.display = 'block'
        }

        // 临时隐藏"长按保存图片"按钮（截图时不显示，但保持占位）
        const slideBtnElement = el.querySelector('.slide-btn')
        if (slideBtnElement) {
          slideBtnElement.style.visibility = 'hidden'
        }

        // 为第七页增加底部 padding，确保内容有足够留白
        const originalPaddingBottom = el.style.paddingBottom
        if (currentPage.key === 'page-7') {
          el.style.paddingBottom = '160px' // 增加底部留白
        }

        // 等待一帧，确保样式生效
        await new Promise(resolve => requestAnimationFrame(resolve))

        // 使用固定高 scale 值（6倍），确保背景图清晰
        const scale = 6 // 固定使用 6 倍缩放

        const canvas = await html2canvas(el, {
          scale: scale, // 高清 scale
          useCORS: true,
          allowTaint: true,
          backgroundColor: '#f8f7f5',
          logging: false,
          imageTimeout: 0,
          width: el.offsetWidth,
          height: elementHeight, // 使用计算后的高度
          windowWidth: el.offsetWidth,
          windowHeight: elementHeight,
          x: 0,
          y: 0,
          foreignObjectRendering: false // 禁用外部对象渲染，提高兼容性
        })

        // 恢复文字样式（保持opacity为1，避免文字消失）
        textElements.forEach(elem => {
          elem.style.opacity = '1'
          elem.style.animation = 'none'
        })

        // 恢复背景图片到 CSS 定义的状态
        if (currentPage.key === 'page-7' || currentPage.key === 'page-new-user') {
          el.style.backgroundImage = originalBackgroundImage // 恢复原始背景图
          el.style.height = ''
          el.style.minHeight = ''
          el.style.position = ''

          // 恢复第七页的 padding
          if (currentPage.key === 'page-7') {
            el.style.paddingBottom = originalPaddingBottom
          }

          // 移除临时插入的背景图元素
          if (bgImageElement && bgImageElement.parentNode) {
            bgImageElement.parentNode.removeChild(bgImageElement)
          }
        }

        // 恢复 overflow 样式（如果之前有保存的话，这里暂时不需要）

        // 恢复二维码隐藏状态
        if (qrElement) {
          qrElement.style.display = ''
        }

        // 恢复底部提示文字隐藏状态
        if (bottomTipElement) {
          bottomTipElement.style.display = ''
        }

        // 恢复"长按保存图片"按钮显示状态
        if (slideBtnElement) {
          slideBtnElement.style.visibility = ''
        }

        // 恢复原始滚动位置
        el.scrollTop = originalScrollTop

        const imgUrl = canvas.toDataURL('image/png', 1.0)

        // 确保loading至少显示2秒
        const elapsedTime = Date.now() - startTime
        const minLoadingTime = 2000
        if (elapsedTime < minLoadingTime) {
          await new Promise(resolve => setTimeout(resolve, minLoadingTime - elapsedTime))
        }

        // 移除遮罩层和loading
        document.body.removeChild(mask)
        this.$toast.clear()
        this.saveImage(imgUrl)

        // 记录用户已生成海报的状态（仅在活动期间）
        if (await isAnnualReportLive()) {
          await this.recordPosterGenerated()
        }
      } catch (error) {
        console.error('生成图片失败:', error)

        // 清理临时背景图元素
        if (bgImageElement && bgImageElement.parentNode) {
          try {
            bgImageElement.parentNode.removeChild(bgImageElement)
          } catch (e) {
            console.error('移除背景图失败:', e)
          }
        }

        // 确保遮罩层被移除
        const mask = document.querySelector('div[style*="z-index: 9999998"]')
        if (mask && mask.parentNode) {
          document.body.removeChild(mask)
        }
        this.$toast.clear()
        this.$toast.fail('生成失败，请重试')
      } finally {
        this.isGeneratingImage = false
      }
    },

    waitImagesLoaded(el) {
      const imgs = el.querySelectorAll('img')
      return Promise.all(
        Array.from(imgs).map(img => {
          if (img.complete) return Promise.resolve()
          return new Promise(resolve => {
            img.onload = img.onerror = resolve
          })
        })
      )
    },

    appendQRCode(originCanvas) {
      const canvas = document.createElement('canvas')
      const ctx = canvas.getContext('2d')

      canvas.width = originCanvas.width
      canvas.height = originCanvas.height

      ctx.drawImage(originCanvas, 0, 0)

      const qrImg = new Image()
      qrImg.src = this.shareQR

      // 二维码尺寸 & 位置（右下角）
      const size = originCanvas.width * 0.18
      const margin = originCanvas.width * 0.05

      ctx.drawImage(
        qrImg,
        canvas.width - size - margin,
        canvas.height - size - margin,
        size,
        size
      )

      return canvas
    },

    saveImage(url) {
      // 创建遮罩层
      const overlay = document.createElement('div')
      overlay.style.position = 'fixed'
      overlay.style.left = '0'
      overlay.style.top = '0'
      overlay.style.width = '100vw'
      overlay.style.height = '100vh'
      overlay.style.zIndex = '9999998'
      overlay.style.background = 'rgba(0, 0, 0, 0.9)'
      overlay.style.display = 'flex'
      overlay.style.flexDirection = 'column'
      overlay.style.alignItems = 'center'
      overlay.style.justifyContent = 'center'

      // 创建图片
      const img = document.createElement('img')
      img.src = url
      img.style.maxWidth = '90vw'
      img.style.maxHeight = '80vh'
      img.style.objectFit = 'contain'

      // 创建提示文字
      const tip = document.createElement('div')
      tip.textContent = '长按图片保存到相册'
      tip.style.color = '#fff'
      tip.style.fontSize = '16px'
      tip.style.marginTop = '20px'
      tip.style.textAlign = 'center'

      // 创建关闭按钮
      const closeBtn = document.createElement('div')
      closeBtn.textContent = '×'
      closeBtn.style.position = 'absolute'
      closeBtn.style.top = '4px'
      closeBtn.style.right = '10px'
      closeBtn.style.color = '#fff'
      closeBtn.style.fontSize = '40px'
      closeBtn.style.cursor = 'pointer'
      closeBtn.style.lineHeight = '1'

      overlay.appendChild(img)
      overlay.appendChild(tip)
      overlay.appendChild(closeBtn)
      document.body.appendChild(overlay)

      // 点击关闭
      const closeOverlay = () => {
        document.body.removeChild(overlay)
      }

      closeBtn.addEventListener('click', closeOverlay)
      overlay.addEventListener('click', (e) => {
        if (e.target === overlay) {
          closeOverlay()
        }
      })
    },
    bindScroll() {
      const shell = this.$refs.scrollShell
      if (shell) {
        shell.addEventListener('wheel', this.handleWheel, {passive: false})
        shell.addEventListener('scroll', this.handleScroll, {passive: true})
      }
    },
    unbindScroll() {
      const shell = this.$refs.scrollShell
      if (shell) {
        shell.removeEventListener('wheel', this.handleWheel)
        shell.removeEventListener('scroll', this.handleScroll)
      }
    },
    handleScroll() {
      const shell = this.$refs.scrollShell
      if (!shell) return

      // 清除之前的定时器
      if (this.scrollTimer) {
        clearTimeout(this.scrollTimer)
      }

      // 延迟更新索引，确保滚动完全停止后才触发动画
      this.scrollTimer = setTimeout(() => {
        const viewH = shell.clientHeight || 1
        const nextIdx = Math.round(shell.scrollTop / viewH)
        if (nextIdx !== this.activePageIndex) {
          this.activePageIndex = nextIdx
        }
      }, 150) // 滚动停止150ms后才触发
    },
    handleWheel(e) {
      const shell = this.$refs.scrollShell
      if (!shell) return
      if (Math.abs(e.deltaY) < 10) return
      e.preventDefault()
      if (this.wheelLock) return
      this.wheelLock = true

      const viewH = shell.clientHeight || 1
      const currentIdx = Math.round(shell.scrollTop / viewH)
      const direction = e.deltaY > 0 ? 1 : -1
      const nextIdx = Math.min(Math.max(currentIdx + direction, 0), this.pageFlow.length - 1)

      const targetTop = nextIdx * viewH
      shell.scrollTo({top: targetTop, behavior: 'smooth'})

      // 延迟触发动画，等待滚动动画完成
      setTimeout(() => {
        this.activePageIndex = nextIdx
        this.wheelLock = false
      }, 500) // 等待滚动动画完成后再触发
    },
    async fetchReport() {
      try {
        this.isDataLoading = true

        // 优先从 URL 参数获取 iepOpenId，方便测试
        const iepOpenId = this.$route.query.iepOpenId || this.userInfo.iepOpenId

        // 获取活动截止日期
        const { endDate } = await getAnnualReportDates()
        if (endDate) {
          this.activityEndDate = endDate
        }

        // 获取第一页数据（注册排名）
        const page1Res = await getRegisterRank(iepOpenId)
        if (page1Res.code !== 0 || !page1Res.data) {
          this.isDataLoading = false
          this.$tipsDialog.alert({
            message: '暂无您的年度报告数据',
            confirmButtonText: '我知道了',
          }).finally(() => {
            this.$router.back()
          })
          return
        }
        const page1Data = page1Res.data

        // 判断是否为新用户（2026年之后注册）
        if (page1Data && page1Data.registerDate) {
          const registerDate = this.$moment(page1Data.registerDate, 'YYYY年MM月DD日')
          this.isNewUser = registerDate.year() >= 2026
        } else {
          this.isNewUser = false
        }

        // 如果是新用户，不需要获取2025年报数据
        if (this.isNewUser) {
          this.reportData = this.transformReportData({report: {}}, null, page1Data)
          this.isDataLoading = false
          this.$nextTick(() => {
            this.markSvgAnimatedByIndex(this.activePageIndex)
            this.markTextAnimatedByIndex(this.activePageIndex)
          })
          return
        }

        // 获取年报数据（不包含第六页）
        const res = await getAnnualReport(2025, iepOpenId)

        // 获取第六页数据（全局统计数据）
        const page6Res = await getAnnualReport(2025, 'ALL')
        const page6Data = (page6Res.code === 0 && page6Res.data && page6Res.data.report) ? page6Res.data.report.page6 : null

        this.reportData = this.transformReportData(res.data || {report: {}}, page6Data, page1Data)

        // 额外检查：如果 page2LastLoginMonth 没有数据，也判定为新用户
        if (!this.reportData.page2LastLoginMonth || this.reportData.page2LastLoginMonth === '-') {
          this.isNewUser = true
          this.reportData = this.transformReportData({report: {}}, null, page1Data)
        }

        // 数据加载完成
        this.isDataLoading = false

        // 数据加载完成后，DOM 被创建，需要手动触发当前页面的动画
        this.$nextTick(() => {
          this.markSvgAnimatedByIndex(this.activePageIndex)
          this.markTextAnimatedByIndex(this.activePageIndex)
        })
      } catch (error) {
        this.$tipsDialog.alert({
          message: '暂无您的年度报告数据',
          confirmButtonText: '我知道了',
        }).finally(() => {
          this.$router.back()
        })
      }
    },
    // 四舍五入处理数字
    roundNumber(value) {
      if (value === '-' || !value) return '-'
      const num = Number(value)
      if (isNaN(num)) return '-'
      return Math.round(num) + '%'
    },
    transformReportData(data, page6Data = null, page1Data = null) {
      const {page2 = {}, page3 = {}, page4 = {}, page5 = {}, page7 = {}} = data.report || {}
      // 优先使用传入的 page1Data
      const page1 = page1Data || {}
      // 优先使用传入的 page6Data
      const page6 = page6Data || {}

      // 解析 "MM月DD日" 格式的日期
      const parseMonthDay = (dateStr) => {
        if (!dateStr) return {month: '-', day: '-'}
        const match = dateStr.match(/(\d+)月(\d+)日/)
        if (match) {
          return {
            month: match[1],
            day: match[2]
          }
        }
        return {month: '-', day: '-'}
      }

      // 解析 "HH点MM分" 格式的时间
      const parseHourMinute = (timeStr) => {
        if (!timeStr) return {hour: '-', minute: '-'}
        const match = timeStr.match(/(\d+)点(\d+)?分?/)
        if (match) {
          return {
            hour: match[1],
            minute: match[2]
          }
        }
        return {hour: '-', minute: '-'}
      }

      // 解析日期为年月日（支持 "YYYY年MM月DD日" 格式）
      const parseYearMonthDay = (dateStr) => {
        if (!dateStr) return {year: '-', month: '-', day: '-'}
        // 尝试匹配 "YYYY年MM月DD日" 格式
        const match = dateStr.match(/(\d+)年(\d+)月(\d+)日/)
        if (match) {
          return {
            year: match[1],
            month: match[2],
            day: match[3]
          }
        }
        // 如果不是中文格式，尝试用 Date 对象解析
        const date = new Date(dateStr)
        if (isNaN(date.getTime())) return {year: '-', month: '-', day: '-'}
        return {
          year: date.getFullYear(),
          month: date.getMonth() + 1,
          day: date.getDate()
        }
      }

      const registerDate = parseYearMonthDay(page1.registerDate)

      // 解析 Page 2 的日期和时间
      const date1 = parseMonthDay(page2.date1)
      const date2 = parseMonthDay(page2.date2)
      const lastLoginDate = parseMonthDay(page2.lastLoginDate)
      const time1 = parseHourMinute(page2.time1)

      // 直接拍平数据结构，字段名为 pageXXX + 原字段名
      return {
        // Page 1 数据
        page1RealName: page1.realName || '-',
        page1RegisterYear: registerDate.year,
        page1RegisterMonth: registerDate.month,
        page1RegisterDay: registerDate.day,
        page1RegisterDays: page1.registerDays,
        page1RegisterRank: page1.registerRank || '-',
        page1FirstLearContent: page1.firstLearContent || '-',

        // Page 2 数据
        page2MostActiveMonth: page2.mostActiveMonth || '-',
        page2Date1Month: date1.month,
        page2Date1Day: date1.day,
        page2Date2Month: date2.month,
        page2Date2Day: date2.day,
        page2Time1Hour: time1.hour,
        page2Time1Minute: time1.minute,
        page2RepeatLearnTimes: page2.repeatLearnTimes || '-',
        page2LastLoginMonth: lastLoginDate.month,
        page2LastLoginDay: lastLoginDate.day,

        // Page 3 数据
        page3AiSearchTimes: page3.aiSearchTimes || '-',
        page3AiSearchSameContentUser: page3.aiSearchSameContentUser || '-',
        page3FollowContentTag: page3.followContentTag || '-',
        page3FollowContentRelatedUserRatio: this.roundNumber(page3.followContentRelatedUserRatio),
        page3MinLearnTimesUsers: page3.minLearnTimesUsers || '-',
        page3MinLearnContent: page3.minLearnContent || '',

        // Page 4 数据
        page4LearnContentTop3: page4.learnContentTop3 || '-',
        page4learnCourseTop1: page4.learnCourseTop1 || '-',
        page4learnCourseTop1FirstDate: page4.learnCourseTop1FirstDate || '-',
        page4LearnCourseTimesTop1: page4.learnCourseTimesTop1 || '-',
        page4LearnCourseLikeUsers: page4.learnCourseLikeUsers || '-',

        // Page 5 数据
        page5LearnAuthorCount: page5.learnAuthorCount || '-',
        page5LearnMaxAuthorName: page5.learnMaxAuthorName || '-',
        page5MaxAuthorLearnDays: page5.maxAuthorLearnDays || '-',
        page5MaxAuthorLearnTimes: page5.maxAuthorLearnTimes || '-',
        page5MaxAuthorLearnMinutes: page5.maxAuthorLearnMinutes || '-',

        // Page 6 数据（优先使用 API 返回的数据，否则使用默认值）
        page6IepRegisterCount: (page6 && page6.iepRegisterCount) || '-',
        page6LearnCount: (page6 && page6.learnCount) || '-',
        page6DingweiCount: (page6 && page6.dingweiCount) || '-',
        page6XuniCount: (page6 && page6.xuniCount) || '-',
        page6LearnContent: (page6 && page6.learnContent) || [],
        page6LearnAuthorTop3: (page6 && page6.learnAuthorTop3) || [],

        // Page 7 数据
        page7CourseCount: page7.courseCount || '-',
        page7LiveCount: page7.liveCount || '-',
        page7CaseCount: page7.caseCount || '-',
        page7CampCount: page7.campCount || '-',
        page7PaperCount: page7.paperCount || '-',
        page7QuestionCount: page7.questionCount || '-',
        page7YearlyLearningTime: this.convertSecondsToMinutes(page7.yearlyLearningTime),
        page7YearlyCourse: page7.yearlyCourse || '-',
        page7YearlyTag: page7.yearlyTag || '-',
        page7LoginCount: page7.loginCount || '-',
        page7UserTotalCost: page7.userTotalCost || '-',
        page7UserCostRank: this.roundNumber(page7.userCostRank),
      }
    },
    goToStudyReport() {
      this.$router.push({path: '/studyReport'})
    },
    initShare() {
      window.sessionStorage.setItem('mainId', '')
      getShareInfo({
        title: 'iEP 2025年度学习报告',
        titleTimeLine: 'iEP 2025年度学习报告',
        desc: '一路相伴 一同成长',
        link: window.location.href,
        linkTimeLine: window.location.href,
        images: 'https://iep.jjmc.cn/bw/file/material/image/2014576577942462466.png'
      })
    },
    async recordPosterGenerated() {
      if (!this.userInfo || !this.userInfo.id) return
      try {
        await postRemind({
          readType: '2025AnnualReportPosterGenerated',
          readTypeName: '2025年度报告海报已生成',
          iepUserId: this.userInfo.id
        })
      } catch (error) {
        console.error('记录海报生成状态失败:', error)
      }
    },
    goToKdSimulator() {
      this.saveScrollPosition()
      this.$router.push({path: '/kdSimulator', query: {entrance: 106, channel: 106}})
    },
    goToECGenius() {
      this.saveScrollPosition()
      this.$router.push({path: '/JJMC_ECGenius', query: {entrance: 106, channel: 106}})
    },
    getContentImage(type) {
      return this.contentImages[type]
    },
    goToLearnContent(item) {
      if (!item || !item.type || !item.id) return

      const type = item.type
      const id = item.id

      // 保存滚动位置
      this.saveScrollPosition()

      if (type === '1') {
        // 跳转到课程详情
        this.$router.push({
          path: '/courserDetails',
          query: {
            courseId: id,
            businessId: '1935545825602424833',
            entrance: 106,
            channel: 106
          }
        })
      } else if (type === '2') {
        let trackParams = `&entrance=106&businessChannel=106&campId=${this.$route.query.areaId}`
        let openidBindli = `&unionid=${this.userInfo.unionId}&openid=${this.userInfo.iepOpenId}${setSha(`xxbiep${this.userInfo.iepOpenId}`)}`
        // 跳转到外部病例分享页面
        window.location.href = `${process.env.bingliUrl}/wap/index.html#/casedetails?id=${id}${openidBindli}${trackParams}`
      } else if (type === '3') {
        // 跳转到文章详情
        this.$router.push({
          path: '/articleDetails',
          query: {
            id: id,
            entrance: 106,
            channel: 106
          }
        })
      }
    },
    /**
     * 保存当前滚动位置
     */
    saveScrollPosition() {
      const shell = this.$refs.scrollShell
      if (!shell) return

      const scrollData = {
        scrollTop: shell.scrollTop,
        activePageIndex: this.activePageIndex,
        svgAnimated: {...this.svgAnimated},
        textAnimated: {...this.textAnimated}
      }

      // 使用 sessionStorage 保存滚动位置和动画状态
      sessionStorage.setItem('annualReport_scrollPosition', JSON.stringify(scrollData))
    },
    /**
     * 恢复滚动位置
     */
    restoreScrollPosition() {
      const savedData = sessionStorage.getItem('annualReport_scrollPosition')
      if (!savedData) return

      try {
        const scrollData = JSON.parse(savedData)

        // 立即恢复动画状态，必须在任何操作之前（避免动画重新播放）
        if (scrollData.svgAnimated) {
          this.svgAnimated = {...this.svgAnimated, ...scrollData.svgAnimated}
        }
        if (scrollData.textAnimated) {
          this.textAnimated = {...this.textAnimated, ...scrollData.textAnimated}
        }

        const shell = this.$refs.scrollShell
        if (shell && scrollData.scrollTop !== undefined) {
          // 使用 nextTick 确保 DOM 已经渲染完成后再恢复滚动位置
          this.$nextTick(() => {
            shell.scrollTop = scrollData.scrollTop
            if (scrollData.activePageIndex !== undefined) {
              this.activePageIndex = scrollData.activePageIndex
            }
            // 清除保存的位置
            sessionStorage.removeItem('annualReport_scrollPosition')
          })
        }
      } catch (error) {
        console.error('恢复滚动位置失败:', error)
      }
    },
    handleReturn() {
      this.$router.replace('/home')
    }
  }
}
export default withRouteTracking(UserProfile)
</script>

<style lang="less" scoped>
/* ===============================
   字体声明
================================ */
@font-face {
  font-family: 'HelloFont WenYiHei';
  src: url('/static/fonts/HelloFont WenYiHei.woff2') format('woff2');
  font-weight: normal;
  font-style: normal;
  font-display: swap;
}

@font-face {
  font-family: 'Source Han Sans SC';
  src: url('/static/fonts/SOURCEHANSANSCN-MEDIUM.woff2') format('woff2');
  font-weight: 500;
  font-style: normal;
  font-display: swap;
}

@font-face {
  font-family: 'Source Han Sans SC';
  src: url('/static/fonts/SOURCEHANSANSCN-BOLD.woff2') format('woff2');
  font-weight: bold;
  font-style: normal;
  font-display: swap;
}

/* ===============================
   页面样式
================================ */
.annual-report {
  min-height: 100vh;
  background: #f8f7f5;
  color: #000;
  padding: 0;
  box-sizing: border-box;
  font-family: 'Source Han Sans SC', 'PingFang SC', 'Helvetica Neue', sans-serif;
  overflow: hidden;

  .return-back-btn {
    position: fixed;
    right: 18px;
    bottom: 147px;
    z-index: 999;
    width: 41px;
    height: 41px;
    cursor: pointer;

    img {
      width: 100%;
      height: 100%;
      object-fit: contain;
    }
  }

  .loading-container {
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 100vh;
    height: 100vh;
    background: #f8f7f5;
    overflow: hidden;
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
  }

  .loading-content {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 24px;
  }

  .loading-logo {
    width: 80px;
    height: 64px;
    object-fit: contain;
    animation: fadeInDown 0.6s ease-out, floatUpDown 2s ease-in-out 0.6s infinite;
  }

  .loading-text {
    font-size: 18px;
    font-weight: 500;
    color: #333;
    font-family: 'Source Han Sans SC', 'PingFang SC', sans-serif;
    animation: fadeIn 1s ease-out 0.4s both;
  }

  .loading-dots {
    display: flex;
    gap: 8px;
    animation: fadeIn 1.2s ease-out 0.6s both;
  }

  .loading-dots .dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: #eb1700;
    animation: dotPulse 1.4s ease-in-out infinite;
  }

  .loading-dots .dot:nth-child(2) {
    animation-delay: 0.2s;
  }

  .loading-dots .dot:nth-child(3) {
    animation-delay: 0.4s;
  }

  @keyframes fadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }

  @keyframes fadeInDown {
    from {
      opacity: 0;
      transform: translateY(-20px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  @keyframes floatUpDown {
    0%, 100% {
      transform: translateY(0);
    }
    50% {
      transform: translateY(-12px);
    }
  }

  @keyframes dotPulse {
    0%, 100% {
      opacity: 0.3;
      transform: scale(0.8);
    }
    50% {
      opacity: 1;
      transform: scale(1.2);
    }
  }

  .scroll-shell {
    height: 100vh;
    overflow-y: auto;
    scroll-snap-type: y mandatory;
    overscroll-behavior: contain;
    -webkit-overflow-scrolling: touch;
    box-sizing: border-box;
  }

  .page-shell {
    position: relative;
    height: 100vh;
    width: 100%;
    scroll-snap-align: start;
    scroll-snap-stop: always;
    overflow: hidden;
  }

  .page-canvas {
    min-height: 100vh;
    width: 100%;
    display: flex;
    flex-direction: column;
    padding: 25px;
    padding-bottom: 16px;
    box-sizing: border-box;
    background-size: 100% 100%;
    background-position: center center;
    background-repeat: no-repeat;
  }

  .page-1 {
    background-image: url('~@/assets/images/activity/iEP-1.jpg');
  }

  .page-2 {
    background-image: url('~@/assets/images/activity/iEP-2.jpg');
  }

  .page-3 {
    background-image: url('~@/assets/images/activity/iEP-3.jpg');
  }

  .page-4 {
    background-image: url('~@/assets/images/activity/iEP-4.jpg');
    height: 100vh;
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;
    background-attachment: local;
  }

  .page-5 {
    background-image: url('~@/assets/images/activity/iEP-5.jpg');
  }

  .page-6 {
    background-image: url('~@/assets/images/activity/iEP-6.jpg');
  }

  .page-7 {
    background-image: url('~@/assets/images/activity/iEP-7.jpg');
    height: 100vh;
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;
    background-attachment: local;
  }

  .page-new-user {
    background-image: url('~@/assets/images/activity/iEP-2.jpg');
    padding: 0;
  }

  .page-brand {
    width: 40px;
    height: 32px;
    display: block;
    background-image: url('~@/assets/images/activity/logo.png');
    background-repeat: no-repeat;
    background-position: left center;
    background-size: contain;
  }

  .page-brand-img {
    width: 40px;
    height: 32px;
    display: block;
    object-fit: contain;
    object-position: left center;
  }

  .page-body {
    display: flex;
    flex-direction: column;
    max-width: 100vh;
    padding-left: 20px;
  }

  .page-footer {
    display: flex;
    flex-direction: column;
    margin-top: auto;
    position: relative;
  }
  .m-t-0 {
    margin-top: 0;
  }
  .m-t-10{
    margin-top: 10px !important;
  }
  .color-fff{
    color: #fff;
  }

  .footer-meta {
    position: absolute;
    display: flex;
    flex-direction: column;
    font-size: 5px;

    &.right {
      align-self: flex-end;
      right: 0;
    }

    &.left {
      align-self: flex-start;
      left: 0;
    }
  }

  .m-r-25 {
    margin-right: 25px;
  }

  /* ===============================
     字体工具类
  ================================ */

  .text-14 {
    font-size: 14px;
  }

  .text-22 {
    font-size: 22px;
  }

  .font-medium {
    font-weight: 500;
  }

  .font-bold {
    font-weight: bold;
  }

  .font-hello {
    font-family: 'HelloFont WenYiHei', 'Source Han Sans SC', 'PingFang SC', sans-serif;
  }

  .text-red {
    color: #eb1700;
  }

  /* ===============================
     通用组件
  ================================ */

  .gradient-line {
    width: 284px;
    height: 2px;
    background: linear-gradient(90deg,
    #f8beb8 36.538%,
    rgba(255, 255, 255, 0.02));
  }

  .slide-btn {
    width: 84px;
    height: 32px;
    border-radius: 8px;
    background: linear-gradient(137.91deg,
    rgba(212, 212, 212, 0.4) 6.05%,
    rgba(212, 212, 212, 1) 92.17%);
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 12px;
    font-weight: 700;
    align-self: center;
  }

  [data-text-seq] {
    opacity: 0;
  }

  .text-animate [data-text-seq] {
    animation-name: fadeInUp, page4BlurIn;
    animation-duration: 0.6s, 0.6s;
    animation-timing-function: ease-out, ease-out;
    animation-fill-mode: both;
    animation-delay: var(--text-delay, 0s), var(--text-delay, 0s);
    will-change: transform, opacity, filter;
  }

  /* ===============================
     Page 1
  ================================ */

  .page1-greeting {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 4px;
    margin-top: 41px;
    margin-bottom: 36px;
  }

  .page1-spark {
    width: 36px;
    height: 22px;
    margin-left: 2px;
  }

  /* ===============================
     Page 2
  ================================ */

  .page2-banner {
    width: 192px;
    height: auto;
    position: absolute;
    left: 32px;
    top: 63px;
  }

  .m-t-28 {
    margin-top: 28px;
  }

  .page2-block {
    display: flex;
    flex-direction: column;
    padding-top: 14px;
    padding-bottom: 14px;
  }

  .m-b-2 {
    margin-bottom: 2px;
  }

  /* ===============================
     Page 3
  ================================ */

  .page3-circle {
    width: 101px;
    height: auto;
    position: absolute;
    top: -18px;
  }

  .l-n-1 {
    line-height: 1;
  }

  .page3-Star1 {
    width: 18px;
    height: 18px;
    position: absolute;
    top: -12px;
    left: 76px;
  }

  .page3-Star2 {
    width: 11px;
    height: 11px;
    position: absolute;
    top: 48px;
    left: 16px;
  }

  .page3-Star3 {
    width: 8px;
    height: 8px;
    position: absolute;
    top: 45px;
    left: 166px;
  }

  .page3-Line {
    width: 85px;
    height: 2px;
    position: absolute;
    top: 48px;
    left: 78px;
  }

  .page3-content {
    position: relative;
    margin-top: 22px;
  }

  .m-t-30 {
    margin-top: 30px;
  }

  .m-b-3 {
    margin-bottom: 3px;
  }

  .page3-card {
    display: flex;
    flex-direction: column;
  }

  /* ===============================
     Page 4
  ================================ */

  .page4-header-box {
    position: relative;
    margin-top: 28px;
  }

  .page4-rank {
    width: 159px;
    height: auto;
    position: absolute;
    top: -14px;
    left: 0;
  }

  .page4-rank-top {
    //width: 52px;
    //height: 22px;
    padding: 3px 0;
    position: absolute;
    top: 0px;
    left: 133px;
    background: #f8f6f5;
  }

  .page4-rank.animate__animated.animate__fadeInUp,
  .page4-rank-top.animate__animated.animate__fadeInUp {
    animation-name: fadeInUp, page4BlurIn;
    animation-duration: 0.6s, 0.6s;
    animation-timing-function: ease-out, ease-out;
    animation-fill-mode: both;
    will-change: transform, opacity, filter;
  }

  .page4-rank-top.animate__animated.animate__fadeInUp {
    animation-delay: 0.08s, 0.08s;
  }

  @keyframes page4BlurIn {
    from {
      filter: blur(6px);
    }
    to {
      filter: blur(0);
    }
  }

  .l-n-12 {
    line-height: 1.2;
  }

  .page4-title-row {
    display: flex;
    align-items: baseline;
    flex-wrap: wrap;
    line-height: 1;
    font-weight: bold;
  }

  .text-mr {
    margin-left: 21px;
    font-weight: bold;
  }

  .page4-rank-list {
    display: flex;
    flex-direction: column;
    gap: 7px;
    margin-top: 26px;
    margin-bottom: 18px;
  }

  .page4-card, .page4-card2 {
    padding: 18px 0;
    display: flex;
    flex-direction: column;
    gap: 3px;
  }

  .page4-card2 {
    padding: 14px 0;
  }

  .page4-course-fav {
    display: flex;
    flex-wrap: wrap;
    gap: 5px;
  }

  /* ===============================
     Page 5
  ================================ */

  .page5-title-row {
    display: flex;
    flex-direction: column;
    gap: 3px;
    font-weight: bold;
    margin-bottom: 32px;
  }

  .text-box {
    position: relative;
  }

  .page5-vector-img {
    width: 76px;
    height: 34px;
    position: absolute;
    bottom: -6px;
  }

  .page5-vector-Star {
    width: 13px;
    height: 13px;
    position: absolute;
    left: 148px;
    bottom: 36px;
  }

  .m-t-14 {
    margin-top: 14px;
  }

  .m-b-12 {
    margin-bottom: 3px;
  }

  .m-b-14 {
    margin-bottom: 14px;
  }

  .m-b-16 {
    margin-bottom: 16px;
  }

  .m-t-16 {
    margin-top: 16px;
  }

  /* ===============================
     Page 6
  ================================ */

  .page6-body {
    display: flex;
    flex-direction: column;
    padding: 0 5px;
  }

  .page6-content {
    padding-left: 15px;
  }

  .page6-title {
    display: flex;
    flex-direction: column;
    margin-top: 30px;
  }

  .p-r {
    position: relative;
  }

  .page6-title-row {
    display: flex;
    align-items: center;
  }

  .page6-vector-img {
    position: absolute;
    top: -28px;
    left: 113px;
    width: 72px;
    height: 64px;
  }

  .m-b-30 {
    margin-bottom: 30px;
  }

  .page6-board {
    margin-top: 16px;
    padding: 10px 18px;
    border-radius: 14px;
    background: rgba(241, 226, 221, 0.7);
    display: flex;
    flex-direction: column;
    gap: 16px;
  }

  .page6-row {
    display: flex;
  }

  .page6-label {
    min-width: 42px;
    font-size: 14px;
    font-weight: 700;
    color: #111;
    display: flex;
    flex-direction: column;
    justify-content: center;
  }

  .page6-cards {
    flex: 1;
    display: flex;
    justify-content: space-evenly;
  }

  .page6-card-1 {
    width: 58px;
    height: 58px;
    margin-top: 3px;
  }

  .page6-card-2{
    width: 77px;
    height: 70px;
  }

  .page6-card2-box {
    text-align: center;
  }

  .page6-card2 {
    width: auto;
    height: 48px;
    object-fit: contain;
  }

  .page6-card-meta {
    font-size: 6px;
  }

  .page6-author {
    display: flex;
    flex-direction: column;
    align-items: center;
  }

  .page6-avatar {
    width: auto;
    height: 42px;
    object-fit: contain;
  }

  .page6-author-name {
    font-size: 6px;
  }

  .page6-author-meta {
    font-size: 6px;
    text-align: center;
  }

  /* ===============================
     Page 7
  ================================ */

  .page7-body {
    padding: 0;
    padding-left: 20px;
  }

  .page-7 {
    padding: 0;
  }

  .page-7-content {
    padding: 25px 0 0 25px;
    background-image: none; /* 默认隐藏，只在截图时显示 */
    background-repeat: no-repeat;
    background-position: center center;
    background-size: cover; /* 使用 cover 确保背景图完整覆盖 */
  }

  .page-7-pd {
    padding-left: 45px;
    padding-right: 45px;
  }

  .m-r-52 {
    margin-right: 52px;
  }

  .page7-title-row {
    display: grid;
    align-items: center;
    font-weight: bold;
    margin-bottom: 18px;
  }

  .page7-badge {
    font-size: 20px;
    text-align: center;
    margin-top: 11px;
    margin-bottom: 11px;
    padding-right: 25px;
  }

  .page7-vector-img {
    width: 120px;
    height: 37px;
    position: absolute;
    top: -3px;
    left: 84px;
    object-fit: contain;
    object-position: left center;
  }

  .page7-vector-img2 {
    width: 120px;
    height: 41px;
    position: absolute;
    top: -11px;
    left: 92px;
    object-fit: contain;
    object-position: left center;
  }

  .page7-mention {
    font-size: 16px;
    font-weight: bold;
  }

  .text-16 {
    font-size: 16px;
  }

  .page7-summary {
    font-size: 14px;
    font-weight: 500;
  }

  .page7-grid {
    display: flex;
    flex-direction: column;
  }

  .page7-metrics {
    display: grid;
  }

  .page7-metric-list {
    display: flex;
    font-size: 14px;
    flex-direction: column;
  }

  .page7-metric-list_item {
    display: grid;
    grid-template-columns: 1fr .9fr 1.6fr;
  }

  .f-w {
    min-width: 0;
    white-space: nowrap;
  }

  .page7-metric-extra {
    display: grid;
    gap: 7px;
    font-size: 14px;
    line-height: 1;
  }

  .page7-cta-wrapper {
    display: flex;
    align-items: center;
    gap: 0;
  }

  .page7-cta {
    font-size: 14px;
    display: grid;
    font-weight: 500;
    gap: 6px;
    line-height: 1;
  }

  .page7-qr {
    width: 70px;
    height: 70px;
    flex-shrink: 0;
    margin-left: 24px;
    display: none; /* 默认隐藏，只在截图时显示 */
  }

  .page7-bottom-tip {
    text-align: center;
    font-size: 14px;
    height: 60px;
    line-height: 60px;
    color: #000;
    display: none; /* 默认隐藏，只在截图时显示 */
  }

  .shareText {
    font-size: 9px;
    text-align: center;
    margin-left: 24px;
  }
  .medal{
    width: 49px;
    height: 49px;
  }
  .medal-box {
    display: flex;
    flex-direction: row;
    justify-content: flex-start;
  }

  .gap-6 {
    display: flex;
    flex-direction: column;
    gap: 6px;
  }

  .gap-3 {
    display: flex;
    flex-direction: column;
    gap: 3px;
  }

  .m-b-6 {
    margin-bottom: 6px;
  }
  .m-b-140 {
    margin-bottom: 140px;
  }

  .m-0 {
    margin: 0;
  }

  .m-t-24 {
    margin-top: 24px;
  }

  .m-t-2 {
    margin-top: 2px;
  }

  /* ===============================
     Page New User
  ================================ */

  .page-new-user-share-content {
    padding: 25px;
    background-image: none; /* 默认隐藏，只在截图时显示 */
    background-repeat: no-repeat;
    background-position: center center;
    background-size: cover; /* 使用 cover 确保背景图完整覆盖 */
  }

  .page-new-user-content {
    margin-top: 60px;
    display: flex;
    flex-direction: column;
  }

  .new-user-box {
    font-size: 14px;
    display: grid;
    font-weight: 500;
    gap: 20px;
    flex: 1;
    line-height: 1;
  }

  .new-user-qr {
    width: 70px;
    height: 70px;
    margin: 0 auto;
    flex-shrink: 0;
    display: none; /* 默认隐藏，只在截图时显示 */
  }

  .pd-r-20{
    padding-right: 20px;
  }

  .new-user-bottom-tip {
    text-align: center;
    font-size: 14px;
    height: 60px;
    line-height: 60px;
    color: #000;
    display: none; /* 默认隐藏，只在截图时显示 */
  }
}

</style>



// WEBPACK FOOTER //
// src/views/annualReport/index.vue